import {
  ESCALATION_KIND_LABELS,
  VIEW_ESCALATIONS_PERMISSION,
  openEscalationWord,
  type OpenEscalationRef,
} from "@estelle/domain";
import { Link } from "@tanstack/react-router";
import { Siren } from "lucide-react";

import { can, usePermissions } from "@/auth/permissions";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/**
 * "Escalated" beside a delivery status (#702): a text label with an icon, so
 * colour is never the only signal. Shown on a row that has an open escalation
 * and on no other.
 */
export function EscalatedLabel({
  className,
  title,
}: {
  className?: string;
  title?: string;
}) {
  return (
    <Badge
      variant="outline"
      title={title}
      className={cn(
        "gap-1.5 border-orange-500/50 font-medium text-orange-800 dark:text-orange-300",
        className,
      )}
    >
      <Siren aria-hidden />
      Escalated
    </Badge>
  );
}

/** The line under an order's shipment card: which escalation is open, with a link to its panel for those who may see it. */
export function ShipmentEscalationLine({
  escalation,
}: {
  escalation: OpenEscalationRef;
}) {
  const permissions = usePermissions();
  const text = `${ESCALATION_KIND_LABELS[escalation.kind]}${escalation.level === null ? "" : `, ${openEscalationWord(escalation).toLowerCase()}`} escalation is open on this parcel.`;
  return (
    <p className="flex flex-wrap items-center gap-2 text-sm">
      <EscalatedLabel />
      <span>
        {can(permissions, VIEW_ESCALATIONS_PERMISSION) ? (
          <Link
            to="/orders/escalations"
            search={{ record: escalation.id }}
            className="text-primary-strong font-medium underline-offset-2 hover:underline"
          >
            {text}
          </Link>
        ) : (
          text
        )}
      </span>
    </p>
  );
}
